import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import MarketingLayout from "@/components/MarketingLayout";
import Seo, { ProductJsonLd } from "@/components/Seo";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Markdown from "@/components/Markdown";
import UsageIndicator from "@/components/UsageIndicator";
import * as Lucide from "lucide-react";
import { toast } from "sonner";

function Icon({ name }) {
  const Cmp = Lucide[name] || Lucide.Sparkles;
  return <Cmp size={22} />;
}

export default function ToolDetail() {
  const { slug } = useParams();
  const { user, refresh } = useAuth();
  const [tool, setTool] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/tools/${slug}`).then((r) => setTool(r.data)).catch(() => setTool(false));
  }, [slug]);

  const run = async () => {
    if (!user) { nav("/login?next=" + encodeURIComponent(`/tools/${slug}`)); return; }
    if (!prompt.trim()) { toast.error("Please enter a prompt"); return; }
    if (user.credits < tool.credit_cost) {
      toast.error("Not enough credits. Buy more to continue.");
      return;
    }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/tools/${slug}/run`, { prompt });
      setResult(data);
      await refresh();
      toast.success(`Generated! ${tool.credit_cost} credits used.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    if (!result) return;
    if (result.kind === "image" && result.image_data_url) {
      const a = document.createElement("a");
      a.href = result.image_data_url;
      a.download = `${slug}.png`;
      a.click();
      return;
    }
    const blob = new Blob([result.output], { type: "text/markdown" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${slug}.md`;
    a.click();
  };

  const copy = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(result.output);
    toast.success("Copied to clipboard");
  };

  if (tool === null) return <MarketingLayout><div className="text-center py-20 text-slate-400">Loading…</div></MarketingLayout>;
  if (tool === false) return <MarketingLayout><div className="text-center py-20 text-slate-400">Tool not found. <Link to="/tools" className="text-purple-300 underline">Back to tools</Link></div></MarketingLayout>;

  return (
    <MarketingLayout>
      <Seo title={tool.name} path={`/tools/${slug}`} description={tool.description} />
      <ProductJsonLd
        name={tool.name}
        description={tool.description}
        image={`https://binarex.co/og-image.png`}
        price={tool.credit_cost}
        currency="credits"
      />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <Link to="/tools" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1"><Lucide.ArrowLeft size={14} /> All Tools</Link>

        <div className="mt-4 flex items-center gap-4">
          <motion.span
            className="bx-glow-icon relative"
            style={{ width: 56, height: 56, borderRadius: 14 }}
            animate={{ y: [-3, 3, -3] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          >
            <motion.span
              aria-hidden
              className="absolute inset-0 rounded-[14px]"
              animate={{ boxShadow: ["0 0 0 0 rgba(139,92,246,0.45)", "0 0 0 14px rgba(139,92,246,0)"] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
            />
            <Icon name={tool.icon} />
          </motion.span>
          <div>
            <h1 className="bx-heading text-3xl sm:text-4xl font-extrabold tracking-tight">{tool.name}</h1>
            <p className="text-slate-400 mt-1">{tool.description}</p>
          </div>
        </div>

        <div className="mt-2 flex flex-wrap gap-2 text-sm">
          <span className="bx-chip">{tool.category}</span>
          <span className="bx-chip"><Lucide.Coins size={12} /> {tool.credit_cost} credits / run</span>
          <span className="bx-chip"><Lucide.Sparkles size={12} /> {tool.kind.toUpperCase()}</span>
          {user && <span className="bx-chip"><Lucide.Wallet size={12} /> Balance: {user.credits.toLocaleString()}</span>}
        </div>

        <div className="mt-8 grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 space-y-4">
          <div className="bx-card p-5">
            <label className="text-sm text-slate-300">Your prompt</label>
            <textarea
              data-testid="tool-prompt-input"
              rows={tool.kind === "image" ? 6 : 10}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={tool.kind === "image" ? "Describe the image you want to generate…" : "Describe what you want to create…"}
              className="bx-input mt-2 resize-y"
            />
            <div className="mt-4 flex items-center justify-between">
              <div className="text-xs text-slate-400">Estimated cost: <span className="text-purple-300 font-mono">{tool.credit_cost} cr</span></div>
              <button data-testid="tool-run-btn" onClick={run} disabled={busy} className="bx-btn-primary">
                {busy ? <><Lucide.Loader2 className="animate-spin" size={16} /> Generating…</> : <><Lucide.Wand2 size={16} /> Generate</>}
              </button>
            </div>
            {!user && (
              <p className="mt-3 text-xs text-amber-300/90"><Lucide.AlertCircle size={12} className="inline mr-1" /> You need to <Link to="/login" className="underline">login</Link> to run AI tools.</p>
            )}
          </div>
          {user && <UsageIndicator compact />}
          </div>

          <div className="lg:col-span-3 bx-card p-5 min-h-[360px] flex flex-col">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-300 font-medium">Result</div>              {result && (
                <div className="flex gap-2">
                  <button data-testid="tool-copy-btn" onClick={copy} className="bx-btn-ghost text-xs"><Lucide.Copy size={14} /> Copy</button>
                  <button data-testid="tool-download-btn" onClick={download} className="bx-btn-ghost text-xs"><Lucide.Download size={14} /> Download</button>
                </div>
              )}
            </div>
            <div className="bx-divider my-4" />
            <div className="flex-1 overflow-auto bx-scrollbar pr-1" data-testid="tool-result-area">
              {busy && (
                <div className="h-full flex items-center justify-center text-slate-400">
                  <div className="text-center">
                    <Lucide.Sparkles className="animate-pulse mx-auto" />
                    <div className="mt-2 text-sm">Crafting your output…</div>
                  </div>
                </div>
              )}
              {!busy && !result && (
                <div className="h-full flex items-center justify-center text-center text-slate-500 py-10">
                  <div>
                    <Lucide.LayoutTemplate className="mx-auto opacity-50" />
                    <p className="mt-2 text-sm">Your generation will appear here.</p>
                  </div>
                </div>
              )}
              {result && result.kind === "image" && result.image_data_url && (
                <img src={result.image_data_url} alt="Generated" className="w-full rounded-xl border border-white/10" />
              )}
              {result && result.kind !== "image" && (
                <Markdown>{result.output}</Markdown>
              )}
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
